import type {
  FormSchema,
  SchemaProperty,
  XEnabledBy,
  XEnabledByCondition,
  XLookup,
  XLookupSource,
} from '@/types/shared/form-schema';
import type { JSONSchema } from '@/types/shared/json-schema';

function schemaIncludesType(
  schema: Exclude<JSONSchema, boolean>,
  expected: string
): boolean {
  if (typeof schema.type === 'string') {
    return schema.type === expected;
  }

  return Array.isArray(schema.type) && schema.type.includes(expected);
}

function resolveSchemaType(schema: Exclude<JSONSchema, boolean>): string {
  if (schemaIncludesType(schema, 'object')) {
    return 'object';
  }

  if (schemaIncludesType(schema, 'array')) {
    return 'array';
  }

  if (schema.properties) {
    return 'object';
  }

  if (schema.items) {
    return 'array';
  }

  if (typeof schema.type === 'string') {
    return schema.type;
  }

  if (Array.isArray(schema.type)) {
    return schema.type.find((type) => type !== 'null') ?? 'string';
  }

  return 'string';
}

/** Extracts a single `XLookupSource` from a raw source object, or `undefined` when incomplete. */
function resolveXLookupSource(raw: {
  'entity-type'?: string;
  'display-key'?: string;
  'submit-key'?: string;
}): XLookupSource | undefined {
  if (!raw['entity-type'] || !raw['display-key'] || !raw['submit-key']) {
    return undefined;
  }
  return {
    entityType: raw['entity-type'],
    displayKey: raw['display-key'],
    submitKey: raw['submit-key'],
  };
}

/**
 * Extracts a validated `XLookup` from a JSON Schema `x-lookup` vendor extension
 * (see ADR-007, ADR-015). The legacy flat shape is normalized into a
 * one-element `sources` array so consumers only ever deal with `sources`. The
 * multi-source path activates when a non-empty `sources` array is present.
 * Returns `undefined` when the extension is absent, has no `form-input-type`, or
 * yields no complete source.
 */
function resolveXLookup(
  schema: Exclude<JSONSchema, boolean>
): XLookup | undefined {
  const raw = schema['x-lookup'];
  if (!raw || !raw['form-input-type']) {
    return undefined;
  }

  const rawSources =
    Array.isArray(raw.sources) && raw.sources.length > 0 ? raw.sources : [raw];

  const sources = rawSources
    .map(resolveXLookupSource)
    .filter((source): source is XLookupSource => source !== undefined);

  if (sources.length === 0) {
    return undefined;
  }

  return {
    formInputType: raw['form-input-type'],
    sources,
  };
}

/**
 * Parses a JSON Schema `x-enabled-by` vendor extension (see ADR-010) into a
 * disjunctive-normal-form `XEnabledBy`. Grammar: `field:value` conditions
 * combined with `&` (AND) and `+` (OR), where `&` binds tighter than `+`.
 * Returns `undefined` when the extension is absent or any condition is
 * malformed (missing `:`, or empty field/value).
 */
function resolveXEnabledBy(
  schema: Exclude<JSONSchema, boolean>
): XEnabledBy | undefined {
  const raw = schema['x-enabled-by'];
  if (typeof raw !== 'string' || raw.trim() === '') return undefined;

  const anyOf: XEnabledByCondition[][] = [];
  for (const group of raw.split('+')) {
    const conditions: XEnabledByCondition[] = [];
    for (const cond of group.split('&')) {
      const separator = cond.indexOf(':');
      if (separator === -1) return undefined;
      const field = cond.slice(0, separator).trim();
      const value = cond
        .slice(separator + 1)
        .trim()
        .toLowerCase();
      if (!field || !value) return undefined;
      conditions.push({ field, value });
    }
    if (conditions.length > 0) anyOf.push(conditions);
  }

  return anyOf.length > 0 ? { anyOf } : undefined;
}

/**
 * Converts a full `JSONSchema` node into the lighter `SchemaProperty` type
 * used by `SchemaFormFields`. Preserves all constraint fields (enum, min/max,
 * pattern, etc.) and recurses into nested objects and array items.
 */
export function jsonSchemaToSchemaProperty(
  schema: JSONSchema
): SchemaProperty | null {
  if (typeof schema === 'boolean') return null;

  const type = resolveSchemaType(schema);

  // Common constraint fields shared across all types
  const constraints: Partial<SchemaProperty> = {
    enum: schema.enum as SchemaProperty['enum'],
    const: schema.const as SchemaProperty['const'],
    default: schema.default as SchemaProperty['default'],
    examples: schema.examples as SchemaProperty['examples'],
    minLength: schema.minLength,
    maxLength: schema.maxLength,
    pattern: schema.pattern,
    minimum: schema.minimum,
    maximum: schema.maximum,
    exclusiveMinimum: schema.exclusiveMinimum,
    exclusiveMaximum: schema.exclusiveMaximum,
  };

  // Strip undefined values so they don't clutter the object
  const defined = Object.fromEntries(
    Object.entries(constraints).filter(([, v]) => v !== undefined)
  );

  const xLookup = resolveXLookup(schema);
  const xEnabledBy = resolveXEnabledBy(schema);

  if (type === 'object') {
    const nestedProperties: Record<string, SchemaProperty> = {};
    if (schema.properties) {
      for (const [key, value] of Object.entries(schema.properties)) {
        const nested = jsonSchemaToSchemaProperty(value);
        if (nested) nestedProperties[key] = nested;
      }
    }
    return {
      type: 'object',
      title: schema.title,
      description: schema.description,
      properties:
        Object.keys(nestedProperties).length > 0 ? nestedProperties : undefined,
      required: schema.required,
      xLookup,
      xEnabledBy,
      ...defined,
    };
  }

  if (type === 'array' && schema.items && typeof schema.items !== 'boolean') {
    const itemsProp = Array.isArray(schema.items)
      ? jsonSchemaToSchemaProperty(schema.items[0])
      : jsonSchemaToSchemaProperty(schema.items);
    return {
      type,
      title: schema.title,
      description: schema.description,
      format: schema.format,
      items: itemsProp ?? undefined,
      xLookup,
      xEnabledBy,
      ...defined,
    };
  }

  return {
    type,
    title: schema.title,
    description: schema.description,
    format: schema.format,
    xLookup,
    xEnabledBy,
    ...defined,
  };
}

/**
 * Converts an object-shaped `JSONSchema` into the `FormSchema` shape used by
 * `SchemaFormFields`. Non-object schemas return `null`.
 */
export function jsonSchemaToFormSchema(
  schema: JSONSchema | undefined
): FormSchema | null {
  if (!schema || typeof schema === 'boolean') return null;
  if (!schema.properties) return null;
  if (schema.type !== undefined && !schemaIncludesType(schema, 'object')) {
    return null;
  }

  const properties: FormSchema['properties'] = {};
  for (const [key, value] of Object.entries(schema.properties)) {
    const property = jsonSchemaToSchemaProperty(value);
    if (!property) continue;
    properties[key] = property;
  }

  return Object.keys(properties).length > 0
    ? { type: 'object', properties, required: schema.required }
    : null;
}

/** Normalizes a form value to the trimmed, lower-cased string used for matching. */
function normalizeMatchValue(value: unknown): string {
  return typeof value === 'string' ? value.trim().toLowerCase() : '';
}

/**
 * Evaluates an `x-enabled-by` expression against the current sibling field
 * values (see ADR-010). Fields without the extension are always enabled; a
 * field with the extension is enabled when *any* AND-group is fully satisfied.
 */
export function isFieldEnabled(
  xEnabledBy: XEnabledBy | undefined,
  values: Record<string, unknown>
): boolean {
  if (!xEnabledBy) return true;
  return xEnabledBy.anyOf.some((group) =>
    group.every(
      (condition) =>
        normalizeMatchValue(values[condition.field]) === condition.value
    )
  );
}
